import { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField,
  Alert, Typography, Autocomplete, Paper, IconButton,
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listClientUsualArticles, addClientUsualArticle, removeClientUsualArticle, type ClientListItem } from '../../api/clienti'
import { listArticles } from '../../api/nomenclatoare'
import { errorMessage } from '../../api/client'

/** FR-MDC-007 — produse cumpărate în mod uzual de client, editabilă, folosită pentru
 * filtrarea implicită (grupare, nu excludere) la introducerea liniei de ofertă/comandă. */
export default function ClientUsualArticlesDialog({ client, onClose }: { client: ClientListItem | null; onClose: () => void }) {
  const open = client !== null
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const { data: usualArticles } = useQuery({
    queryKey: ['comercial', 'produse-uzuale', client?.id],
    queryFn: () => listClientUsualArticles(client!.id),
    enabled: open,
  })
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })

  const [articleId, setArticleId] = useState<string | null>(null)
  const usedIds = new Set(usualArticles?.map((u) => u.articleId))
  const availableArticles = articles?.filter((a) => !usedIds.has(a.id)) ?? []

  const addMutation = useMutation({
    mutationFn: () => addClientUsualArticle(client!.id, articleId!),
    onSuccess: () => {
      setArticleId(null)
      void qc.invalidateQueries({ queryKey: ['comercial', 'produse-uzuale', client?.id] })
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const removeMutation = useMutation({
    mutationFn: (usualArticleId: string) => removeClientUsualArticle(usualArticleId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['comercial', 'produse-uzuale', client?.id] }),
    onError: (e) => setError(errorMessage(e)),
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Produse uzuale — {client?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
            <Autocomplete
              sx={{ flexGrow: 1 }} options={availableArticles.map((a) => a.id)}
              getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
              value={articleId} onChange={(_, v) => setArticleId(v)}
              renderInput={(params) => <TextField {...params} label="Articol" size="small" />}
            />
            <Button variant="outlined" startIcon={<Add />} disabled={!articleId || addMutation.isPending}
              onClick={() => addMutation.mutate()}>
              Adaugă
            </Button>
          </Stack>

          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          <Stack spacing={1}>
            {usualArticles?.length === 0 && <Typography variant="body2" color="text.secondary">Niciun produs uzual încă.</Typography>}
            {usualArticles?.map((u) => (
              <Paper key={u.id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.articleCode} — {u.articleDescription}</Typography>
                <IconButton size="small" onClick={() => removeMutation.mutate(u.id)} disabled={removeMutation.isPending}>
                  <Delete fontSize="small" />
                </IconButton>
              </Paper>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
      </DialogActions>
    </Dialog>
  )
}
